import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Autocomplete, Button, IconButton, Stack, Text, Card } from '@contentful/f36-components';
import { DeleteIcon } from '@contentful/f36-icons';
import type { Attribute } from '../types/types';

export type AttributeSelectorProps = {
  attributes: Attribute[];
  initialAttribute: Attribute;
  onRemoveAttribute: (id: string) => void;
  id: string;
  onSelectedAttributeChange: (attribute: Attribute, id: string) => void;
};

export const AttributeSelector: React.FC<AttributeSelectorProps> = ({
  attributes,
  initialAttribute,
  onRemoveAttribute,
  onSelectedAttributeChange,
  id
}: AttributeSelectorProps) => {
  const [filteredAttributes, setFilteredAttributes] = useState<Attribute[]>(attributes);
  const [selectedAttribute, setSelectedAttribute] = useState<Attribute>(initialAttribute);
  const [filteredAttributeValues, setFilteredAttributeValues] = useState<string[]>([]);
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<string[]>(initialAttribute.values);
  const previousSelectedAttribute = useRef(initialAttribute);

  const getAttributeValues = (attributeName: string): string[] => {
    const attribute = attributes.find((a) => a.name == attributeName);
    if (attribute) {
      return attribute.values;
    }
    return [];
  };

  const attributeValues = useMemo<string[]>(() => getAttributeValues(selectedAttribute.name), [selectedAttribute]);

  useEffect(() => {
    if (previousSelectedAttribute.current.name != selectedAttribute.name) {
      setFilteredAttributeValues(selectedAttribute.values);
      setSelectedAttributeValues([]);
      previousSelectedAttribute.current = selectedAttribute;
    }
  }, [selectedAttribute]);

  useEffect(() => {
    const attributeValues = getAttributeValues(initialAttribute.name);
    setFilteredAttributeValues(attributeValues);
  }, []);

  useEffect(() => {
    onSelectedAttributeChange({ name: selectedAttribute.name, values: selectedAttributeValues }, id);
  }, [selectedAttributeValues]);

  const handleSearchAttribute = (value: string) => {
    const newFilteredAttributes = attributes.filter((a) =>
      a.name.toLocaleLowerCase().includes(value.toLocaleLowerCase())
    );
    setFilteredAttributes(newFilteredAttributes);
  };

  const handleSelectAttribute = (attribute: Attribute) => {
    setSelectedAttribute(attribute);
  };

  const handleSearchAttributeValues = (value: string) => {
    const newFilteredAttributeValues = attributeValues.filter((v) =>
      v.toLocaleLowerCase().includes(value.toLocaleLowerCase())
    );
    setFilteredAttributeValues(newFilteredAttributeValues);
  };

  const handleSelectAttributeValues = (value: string) => {
    if (!selectedAttributeValues.includes(value)) {
      const updatedselectedAttributeValues = [...selectedAttributeValues];
      updatedselectedAttributeValues.push(value);
      setSelectedAttributeValues(updatedselectedAttributeValues);
    }
  };

  const removeAttributeValue = (valueToRemove: string) => {
    const newAttributeValues = selectedAttributeValues.filter((f) => f != valueToRemove);
    setSelectedAttributeValues(newAttributeValues);
  };

  const getSelectedAttribute = () => {
    if (!selectedAttribute) {
      return null;
    }

    return (
      <Text fontSize="fontSizeM">
        Selected attribute: <Text fontWeight="fontWeightDemiBold">{selectedAttribute.name}</Text>
      </Text>
    );
  };

  return (
    <Card style={{ margin: '0 auto' }}>
      <Stack style={{ width: '100%' }} alignItems="start" justifyContent="space-between">
        <Stack
          flexDirection="column"
          style={{ width: '50%', height: '100%' }}
          alignItems="start"
          justifyContent="space-between">
          <Autocomplete
            items={filteredAttributes}
            itemToString={(item) => item.name}
            renderItem={(item) => item.name}
            onInputValueChange={handleSearchAttribute}
            onSelectItem={handleSelectAttribute}
            clearAfterSelect
          />
          {getSelectedAttribute()}
          <Button onClick={() => onRemoveAttribute(id)} variant="negative">
            Remove filter
          </Button>
        </Stack>
        <Stack flexDirection="column" style={{ width: '40%' }} alignItems="end">
          <Autocomplete
            items={filteredAttributeValues}
            onInputValueChange={handleSearchAttributeValues}
            onSelectItem={handleSelectAttributeValues}
            clearAfterSelect
          />
          {selectedAttributeValues?.map((attributeValue) => (
            <Stack key={`remove-${id}-${attributeValue}`} spacing="none">
              <Text fontSize="fontSizeM">{attributeValue}</Text>
              <IconButton
                variant="transparent"
                icon={<DeleteIcon variant="negative" size="small" />}
                aria-label="Remove attribute value"
                onClick={() => removeAttributeValue(attributeValue)}
                style={{ padding: '0.5em' }}
              />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};
